import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Loader2, Send, CheckCircle2, XCircle, Inbox } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export const REFUND_STEPS = ['requested', 'processing', 'sent', 'arrived'];

const STATUS_META = {
  requested: { label: 'Requested', hint: 'We review requests within 1–2 business days', icon: Inbox, color: 'text-amber-300', bg: 'bg-amber-500/10 border-amber-500/30' },
  processing: { label: 'Processing', hint: 'Stripe is finishing your refund', icon: Loader2, color: 'text-sky-300', bg: 'bg-sky-500/10 border-sky-500/30' },
  sent: { label: 'Sent to your card', hint: 'On its way to your card', icon: Send, color: 'text-[#C8A6FF]', bg: 'bg-[#9333EA]/10 border-[#9333EA]/30' },
  arrived: { label: 'Should have arrived', hint: 'Check your card statement', icon: CheckCircle2, color: 'text-emerald-300', bg: 'bg-emerald-500/10 border-emerald-500/30' },
  failed: { label: "Couldn't complete", hint: 'Contact support and we\'ll sort it out', icon: XCircle, color: 'text-red-300', bg: 'bg-red-500/10 border-red-500/30' },
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : null;

export function useMyRefunds(goalId) {
  const [refunds, setRefunds] = useState(null);
  useEffect(() => {
    const params = goalId ? { goal_id: goalId } : {};
    axios.get(`${API}/api/refunds/mine`, { params, withCredentials: true })
      .then((r) => setRefunds(r.data.refunds || []))
      .catch(() => setRefunds([]));
  }, [goalId]);
  return refunds;
}

function StepBar({ status }) {
  const idx = REFUND_STEPS.indexOf(status);
  return (
    <div className="flex gap-1 mt-3" data-testid="refund-step-bar">
      {REFUND_STEPS.map((s, i) => (
        <div key={s} className={`h-1 flex-1 rounded-full transition-colors duration-500 ${status === 'failed' ? 'bg-red-500/40' : i <= idx ? 'bg-[#9333EA]' : 'bg-white/10'}`} />
      ))}
    </div>
  );
}

function ArrivalPrompt({ refund }) {
  const [state, setState] = useState(refund.arrival_confirmed ? 'confirmed' : refund.missing_reported ? 'reported' : 'ask');
  const [reporting, setReporting] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const act = async (path, next, payload = {}) => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/refunds/${refund.id}/${path}`, payload, { withCredentials: true });
      setState(next);
      toast.success(next === 'confirmed' ? 'Thanks for confirming!' : "Got it. We're tracking down your refund.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (state === 'confirmed') return <p className="mt-3 text-xs text-emerald-300 font-semibold" data-testid="refund-arrival-confirmed">You confirmed this refund arrived</p>;
  if (state === 'reported') return <p className="mt-3 text-xs text-amber-300 font-semibold" data-testid="refund-missing-reported">Reported missing — we'll email you within 1–2 business days</p>;
  return (
    <div className="mt-3 pt-3 border-t border-white/10" data-testid="refund-arrival-prompt">
      <p className="text-xs text-white font-semibold mb-2">Did your refund arrive?</p>
      {reporting ? (
        <div className="space-y-2">
          <textarea data-testid="refund-missing-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Anything we should know? (optional)" className="w-full rounded-lg bg-[#0A0A0F] border border-white/10 p-2 text-xs text-white placeholder:text-[#6F6981] focus:outline-none focus:border-[#9333EA]" rows={2} />
          <div className="flex gap-2">
            <button data-testid="refund-missing-submit-btn" disabled={busy} onClick={() => act('report-missing', 'reported', { note })} className="px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-200 text-xs font-semibold hover:bg-red-500/30 transition-colors disabled:opacity-50">Send report</button>
            <button data-testid="refund-missing-cancel-btn" onClick={() => setReporting(false)} className="px-3 py-1.5 rounded-full text-[#A19DB0] text-xs hover:text-white transition-colors">Cancel</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-2">
          <button data-testid="refund-confirm-arrived-btn" disabled={busy} onClick={() => act('confirm-arrived', 'confirmed')} className="px-3 py-1.5 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 text-xs font-semibold hover:bg-emerald-500/30 transition-colors disabled:opacity-50">Yes, it arrived</button>
          <button data-testid="refund-report-missing-btn" onClick={() => setReporting(true)} className="px-3 py-1.5 rounded-full border border-white/15 text-[#A19DB0] text-xs font-semibold hover:text-white hover:border-white/30 transition-colors">Report it missing</button>
        </div>
      )}
    </div>
  );
}

export function RefundStatusCard({ refund, showVault = false }) {
  const meta = STATUS_META[refund.status] || STATUS_META.sent;
  const Icon = meta.icon;
  return (
    <div className={`p-4 rounded-xl border ${meta.bg}`} data-testid={`refund-card-${refund.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Icon className={`h-5 w-5 mt-0.5 shrink-0 ${meta.color} ${refund.status === 'processing' ? 'animate-spin' : ''}`} />
          <div>
            <p className={`font-semibold text-sm ${meta.color}`} data-testid="refund-status-label">{meta.label}</p>
            {showVault && <p className="text-xs text-[#A19DB0] mt-0.5" data-testid="refund-vault-name">{refund.goal_name}</p>}
            <p className="text-xs text-[#A19DB0] mt-0.5">{meta.hint}</p>
          </div>
        </div>
        <span className="font-mono text-lg font-bold text-white shrink-0" data-testid="refund-amount">${refund.amount.toFixed(2)}</span>
      </div>
      <StepBar status={refund.status} />
      {refund.eta && refund.status !== 'failed' && (
        <div className="flex items-center gap-1.5 mt-3 text-xs text-[#A19DB0]" data-testid="refund-eta">
          <Clock className="h-3.5 w-3.5" />
          {refund.status === 'arrived' ? 'Expected by ' : 'Estimated arrival by '}
          <span className="text-white font-semibold">{fmtDate(refund.eta)}</span>
        </div>
      )}
      {refund.status === 'arrived' && <ArrivalPrompt refund={refund} />}
    </div>
  );
}

export function VaultRefundTracker({ goalId, onLoaded }) {
  const refunds = useMyRefunds(goalId);
  useEffect(() => { if (refunds) onLoaded?.(refunds.length); }, [refunds, onLoaded]);
  if (!refunds || refunds.length === 0) return null;
  return (
    <div className="space-y-2" data-testid="vault-refund-tracker">
      {refunds.map((r) => <RefundStatusCard key={r.id} refund={r} />)}
    </div>
  );
}
